import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";
import { useSharedTranslation } from "@/shared/contexts/TranslateContext";

import { joinObjArrays } from "../utils/array";
import EgwWebFont from "../assets/EgwWebFont";
import IconView from "../components/IconView/IconView";

import { _sharedActionsSupport } from "../store/support/actions";
import { _sharedActionsSystem } from "../store/system/actions";
import { useLoaderById } from "../hooks/useLoaderById";
import TextField from "../components/TextField/TextField";
import Button from "../components/Button/Button";
import { CFStatus, ComponentsId, FontFamily, TOAST_TYPE } from "../constants";
import {
  defaultContactTag,
  emailRegExp,
  fileFormatRegex,
  initErrors,
  initValues,
  MAX_FILE_SIZE,
} from "../utils/contact-us/constants";
import SelectVariantButton from "../components/Select/SelectVariantButton";
import { getOptionKey } from "../utils/select";
import Scroll from "../components/Scroll/Scroll";
import { LinkLearnHowToUseApps } from "@/shared/components/Button/LinkLearnHowToUseApps";
import { Loader } from "@/shared/components";
import {
  selectorSupportFormSentStatus,
  selectorSupportMainGeneralQuestions
} from "@/shared/store/support/selectors";

import styles from "./ContactScreen.module.scss";

const ContactScreen = ({
  width,
  isMobile,
  isTablet,
  loaderProps,
  withNoMargin = false,
  isStaticWidth = false,
}) => {
  const { t } = useSharedTranslation();
  const dispatch = useDispatch();
  const [errors, setErrors] = useState(initErrors);
  const [values, setValues] = useState(initValues);
  const [files, setFiles] = useState([]);
  const [dragging, setDragging] = useState(false);

  const mainGeneralQuestions = useSelector(selectorSupportMainGeneralQuestions);
  const formSentStatus = useSelector(selectorSupportFormSentStatus);
  const isLoading = useLoaderById(ComponentsId.CF_SEND_ID);

  const isMobileOrTablet = isMobile || isTablet;

  useEffect(() => {
    dispatch(_sharedActionsSupport.actionFetchCF());
  }, []);

  useEffect(() => {
    if (!mainGeneralQuestions.length) {
      return;
    }

    const defaultContact = mainGeneralQuestions.find(
      (contact) => contact.tag === defaultContactTag,
    );

    setValues({ ...values, contact: defaultContact || mainGeneralQuestions[0] });
  }, [mainGeneralQuestions]);

  useEffect(() => {
    if (formSentStatus === CFStatus.PENDING) {
      return;
    }

    if (formSentStatus === CFStatus.SUCCESS) {
      dispatch(_sharedActionsSystem.toast(t("your_mail_sent"), TOAST_TYPE.success));
    } else {
      dispatch(_sharedActionsSystem.toast(t("sendEmailError"), TOAST_TYPE.error));
    }
  }, [formSentStatus]);

  const handleChange = (item) => {
    const option = mainGeneralQuestions.find((o) => getOptionKey(o) === item);
    if (option) {
      setValues({ ...values, contact: option });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !errors.contact &&
      !errors.name &&
      !errors.email &&
      !errors.message &&
      values.contact &&
      values.name &&
      values.email &&
      values.message
    ) {
      const formData = new FormData();
      Object.keys(values).forEach((key) =>
        formData.append(key, key === "contact" ? values[key].tag : values[key]),
      );
      if (values.contact.tag === "bug") {
        files.forEach((file, index) => {
          formData.append("data" + index, file);
        });
      }

      dispatch(_sharedActionsSupport.actionSendCF(formData));

      setValues({ ...values, name: "", email: "", message: "" });
      setFiles([]);
      dispatch(_sharedActionsSupport.actionSendCFStatus(CFStatus.PENDING));
    } else {
      let newErrors = {};
      if (!values.name || values.name === "") {
        newErrors.name = t("error_message_length", { field: "name", length: 3 });
      }
      if (!values.email || values.email === "") {
        newErrors.email = t("error_message_email");
      }
      if (!values.message || values.message === "") {
        newErrors.message = t("error_message_required", { field: "message" });
      }
      if (Object.keys(newErrors).length > 0) {
        setErrors({ ...errors, ...newErrors });
      }
    }
  };

  const cancelEvent = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    cancelEvent(e);
    if (!dragging) {
      setDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    cancelEvent(e);
    if (dragging) {
      setDragging(false);
    }
  };

  const handleFileDrop = (e) => {
    cancelEvent(e);
    const newFiles = [];
    const fileItems = e.dataTransfer.items;
    if (fileItems) {
      for (var i = 0; i < fileItems.length; i++) {
        if (fileItems[i].kind === "file") {
          newFiles.push(fileItems[i].getAsFile());
        }
      }
    }
    appendFiles(newFiles);
    setDragging(false);
  };

  const appendFiles = (incomingFiles) => {
    const allFiles = joinObjArrays([incomingFiles, files], "name");
    let totalSize = 0;
    if (allFiles.length > 5) {
      dispatch(_sharedActionsSystem.toast(t("canNotAttachMoreFiles"), TOAST_TYPE.error));
      return;
    }
    allFiles.forEach((file) => {
      totalSize += file.size;
    });
    if (totalSize > MAX_FILE_SIZE) {
      dispatch(_sharedActionsSystem.toast(t("filesCanNotBeBigger"), TOAST_TYPE.error));
      return;
    }
    const filesToAppend = [];
    let showWrongFormatMsg = false;
    incomingFiles.forEach((file) => {
      if (fileFormatRegex.test(file.name.toLowerCase())) {
        filesToAppend.push(file);
      } else {
        showWrongFormatMsg = true;
      }
    });
    if (showWrongFormatMsg) {
      dispatch(_sharedActionsSystem.toast(t("supportedFileFormats"), TOAST_TYPE.error));
    }
    if (filesToAppend.length > 0) {
      setFiles(joinObjArrays([filesToAppend, files], "name"));
    }
  };

  const handleFileRemove = (file) => {
    const deleteIndex = files.findIndex((item) => item.name === file.name);
    if (deleteIndex !== -1) {
      files.splice(deleteIndex, 1);
      setFiles([...files]);
    }
  };

  const handleTextFieldBlur = (field, validate) => {
    // EGWW-3944
    const error = validate ? validate(field, values[field].trim()) : "";
    setValues({ ...values, [field]: values[field].trim() });
    setErrors({ ...errors, [field]: error });
  };

  const renderField = (field, fieldLabel, { rows, validate } = {}, isTabletAlbum) => {
    return (
      <div
        className={classNames(styles["contact-form-row"], styles[`field-${field}`], {
          [styles["tablet-album"]]: isTabletAlbum,
        })}>
        <label className={styles["label-form"]}>{t(fieldLabel)}:</label>
        <TextField
          name={field}
          style={{ flexBasis: !isMobile ? "60%" : "100%" }}
          value={values[field]}
          error={!!errors[field]}
          disabled={isLoading}
          onBlur={() => handleTextFieldBlur(field, validate)}
          multiline={!!rows}
          helperText={errors[field]}
          rows={rows ? rows : 1}
          rootClass="note-textarea"
          onChange={({ target: { name, value } }) => setValues({ ...values, [name]: value })}
        />
      </div>
    );
  };

  const renderFileAttach = () => {
    return (
      <div
        className={classNames(styles["drop-view"], { [styles["on-drag"]]: dragging })}
        onDragEnter={handleDragEnter}
        onDrop={handleFileDrop}
        onDragOver={cancelEvent}
        onDragLeave={handleDragLeave}>
        <label htmlFor="files-upload" className={styles["cf-label-file"]}>
          <IconView icon={EgwWebFont.clip} />
          &nbsp;
          {t("attachFiles")}
        </label>
        <input
          type="file"
          name="files"
          id="files-upload"
          className={styles["files-upload"]}
          multiple
          onChange={(e) => appendFiles(Array.from(e.target.files))}
        />
        <span className={styles["attach-desc"]}>{t("fileLimitationsMsg")}</span>
      </div>
    );
  };

  const renderFileItem = (file, index) => {
    return (
      <div
        className={classNames(styles["cf-file-item"], {
          [styles["tablet"]]: isTablet,
          [styles["mobile"]]: isMobile,
        })}
        key={index.toString()}>
        <span className={styles["cf-file-title"]}>
          <IconView icon={EgwWebFont.file} />
          &nbsp;
          {file.name}
        </span>
        <Button type="button" onClick={() => handleFileRemove(file)} title={t("close")}>
          <IconView icon={EgwWebFont.close} />
        </Button>
      </div>
    );
  };

  const renderFiles = () => {
    let content;
    if (width < 890 || files.length === 1) {
      content = files.map((file, index) => renderFileItem(file, index));
    } else {
      const leftColumn = [];
      const rightColumn = [];
      files.forEach((file, index) => {
        const column = index % 2 === 0 ? leftColumn : rightColumn;
        column.push(file);
      });
      content = (
        <React.Fragment>
          <div className={styles["cf-files-column"]}>
            {leftColumn.map((file, index) => renderFileItem(file, index))}
          </div>
          <div className={styles["cf-files-column"]}>
            {rightColumn.map((file, index) => renderFileItem(file, index))}
          </div>
        </React.Fragment>
      );
    }
    return (
      <div
        className={classNames(styles["cf-files-container"], { [styles["column"]]: width < 750 })}>
        {content}
      </div>
    );
  };

  const renderContent = () => {
    const isDesktopSmall = !isMobileOrTablet && width < 750;
    const isTabletAlbum = isTablet && width > 768;
    const isBagReport = values.contact.tag === "bug";

    const isScrollWrapper = !isMobile && Scroll;
    const WrapperComponent = isScrollWrapper ? Scroll : "div";
    const WrapperProps = isScrollWrapper ? { hasStaticPosition: true } : undefined;

    return (
      <WrapperComponent {...WrapperProps}>
        <div className={classNames(styles["contentMain"], styles["contact-page"], "contact-page")}>
          <div
            className={classNames(styles["contact-form-wrap"], {
              [styles["static-width"]]: isStaticWidth,
              [styles["no-margin"]]: (withNoMargin && isDesktopSmall) || isTablet,
            })}>
            {isLoading ? (
              <Loader {...loaderProps} />
            ) : (
              <form onSubmit={handleSubmit}>
                <div
                  className={classNames(styles["fields-container"], {
                    [styles["tablet-album"]]: isTabletAlbum,
                  })}>
                  {renderField(
                    "name",
                    "enter_your_name",
                    {
                      validate: (k, v) =>
                        v.length < 3 ? t("error_message_length", { field: k, length: 3 }) : "",
                    },
                    isTabletAlbum,
                  )}
                  {renderField(
                    "email",
                    "enter_your_email",
                    {
                      validate: (k, v) => (!emailRegExp.test(v) ? t("error_message_email") : ""),
                    },
                    isTabletAlbum,
                  )}
                </div>
                <div className={styles["drop-down-container"]}>
                  {t("chooseSubject")}:&nbsp;
                  <SelectVariantButton
                    label={values.contact?.name?.en}
                    buttonProps={{
                      variant: Button.variant.clear,
                      color: Button.color.secondary,
                      fontFamily: FontFamily.roboto,
                      style: {
                        textTransform: "none",
                      },
                    }}
                    value={values.contact}
                    options={mainGeneralQuestions.map((o) => {
                      return {
                        ...o,
                        label: o.name.en,
                      };
                    })}
                    onChange={handleChange}
                  />
                </div>
                {renderField("message", "enter_your_message", {
                  rows: 5,
                  validate: (k, v) =>
                    v.length === 0 ? t("error_message_required", { field: k }) : "",
                })}
                {isBagReport && renderFileAttach()}
                {isBagReport && files.length > 0 && renderFiles()}
                <div className={classNames(styles["contact-form-row"], styles["with-submit"])}>
                  <Button
                    disabled={isLoading}
                    onClick={handleSubmit}
                    color={Button.color.primary}
                    className={classNames(styles["contact-form-send"], {
                      [styles["mobile"]]: isMobile,
                    })}>
                    {t("send")}
                  </Button>
                </div>
              </form>
            )}
            <LinkLearnHowToUseApps />
          </div>
        </div>
      </WrapperComponent>
    );
  };

  return renderContent();
};

export default ContactScreen;
